import React, { useMemo, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

const numberFormat = { type: "fixedPoint", precision: 2 };

/**
 * Reusable WM item-master grid backed by wm/Item/GetAllItems (server-side
 * DataSourceLoader), filtered by the customer's identity rowid (`customerId`
 * → R_Identity / RCustomer). Includes IMItem master + inventory-status columns.
 */
const CustomerItems = (props) => {
    const { customerId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const allowedPageSizes = [10, 20, 100, 500, "all"];
    const stateKey = gridKey;
    const loadState = useCallback(() => GridLayout.sendStorageRequest(stateKey, "GET", accessToken, null, currentLayout), [stateKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(stateKey, "POST", accessToken, state, currentLayout), [stateKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!customerId) {
            return [];
        }
        const store = createStore({
            key: 'rowId',
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.Item.GetAllItems}`,
            loadParams: { RCustomer: customerId },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        return new DataSource({ store });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            dataSource={dataSource}
            keyExpr="rowId"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
            onSelectionChanged={(e) => {
                if (props.onItemSelected) {
                    const row = e.selectedRowsData && e.selectedRowsData.length === 1 ? e.selectedRowsData[0] : null;
                    props.onItemSelected(row ? row.rowId : null);
                }
            }}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={stateKey} customSave={saveState} />

            {/* Default-visible columns (mirrors the AI8 item layout) */}
            <Column dataField="itemCode" caption="Item" />
            <Column dataField="itemDescription1" caption="Item Description - 1" />
            <Column dataField="itemDescription2" caption="Item Description - 2" />
            <Column dataField="classCode" caption="Class" />
            <Column dataField="subClassCode" caption="Sub Class" />
            <Column dataField="itemAvailable" caption="Available" dataType="number" format={numberFormat} />

            {/* Column-chooser (hidden by default) */}
            <Column dataField="itemInBackOrder" caption="BO-Receiving" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemOutBackOrder" caption="BO-Shipping" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemUDFCode1" caption="Code 1" visible={false} />
            <Column dataField="itemUDFCode2" caption="Code 2" visible={false} />
            <Column dataField="itemUDFCode3" caption="Code 3" visible={false} />
            <Column dataField="itemUDFCode4" caption="Code 4" visible={false} />
            <Column dataField="itemUDFCode5" caption="Code 5" visible={false} />
            <Column dataField="itemUDFCode6" caption="Code 6" visible={false} />
            <Column dataField="itemUDFCode7" caption="Code 7" visible={false} />
            <Column dataField="itemUDFCode8" caption="Code 8" visible={false} />
            <Column dataField="itemUDFCode9" caption="Code 9" visible={false} />
            <Column dataField="itemUDFCode10" caption="Code 10" visible={false} />
            <Column dataField="creationUserId" caption="Created By" visible={false} />
            <Column dataField="creationDateTime" caption="Creation Date" dataType="date" visible={false} />
            <Column dataField="itemUDFDate1" caption="Date 1" dataType="date" visible={false} />
            <Column dataField="itemUDFDate2" caption="Date 2" dataType="date" visible={false} />
            <Column dataField="itemUDFDate3" caption="Date 3" dataType="date" visible={false} />
            <Column dataField="itemUDFDate4" caption="Date 4" dataType="date" visible={false} />
            <Column dataField="itemUDFDate5" caption="Date 5" dataType="date" visible={false} />
            <Column dataField="itemUDFDate6" caption="Date 6" dataType="date" visible={false} />
            <Column dataField="itemUDFDate7" caption="Date 7" dataType="date" visible={false} />
            <Column dataField="itemUDFDate8" caption="Date 8" dataType="date" visible={false} />
            <Column dataField="itemUDFDate9" caption="Date 9" dataType="date" visible={false} />
            <Column dataField="itemUDFDate10" caption="Date 10" dataType="date" visible={false} />
            <Column dataField="dateExpiration" caption="Expiration Dt" dataType="date" visible={false} />
            <Column dataField="freightClassCode" caption="Freight Class" visible={false} />
            <Column dataField="itemInFuture" caption="Future Rec" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemOutFuture" caption="Future Ship" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemInReceiving" caption="In-Receiving" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemOutShipped" caption="In-Shipping" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="inventoryUnitUOM" caption="Inventory Unit UOM" visible={false} />
            <Column dataField="lastUpdateDateTime" caption="Last Updated" dataType="date" visible={false} />
            <Column dataField="lastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="module" caption="Module" visible={false} />
            <Column dataField="itemNetAvailable" caption="Net Available" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemOnHand" caption="On-Hand" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemInOrdered" caption="Pending Rec" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemOutOrdered" caption="Pending Ship" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemUDFQty1" caption="Quantity 1" dataType="number" visible={false} />
            <Column dataField="itemUDFQty2" caption="Quantity 2" dataType="number" visible={false} />
            <Column dataField="itemUDFQty3" caption="Quantity 3" dataType="number" visible={false} />
            <Column dataField="itemUDFQty4" caption="Quantity 4" dataType="number" visible={false} />
            <Column dataField="itemUDFQty5" caption="Quantity 5" dataType="number" visible={false} />
            <Column dataField="itemUDFQty6" caption="Quantity 6" dataType="number" visible={false} />
            <Column dataField="itemUDFQty7" caption="Quantity 7" dataType="number" visible={false} />
            <Column dataField="itemUDFQty8" caption="Quantity 8" dataType="number" visible={false} />
            <Column dataField="itemUDFQty9" caption="Quantity 9" dataType="number" visible={false} />
            <Column dataField="itemUDFQty10" caption="Quantity 10" dataType="number" visible={false} />
            <Column dataField="receiptPackSize" caption="Receipt Pack Size" visible={false} />
            <Column dataField="receiptUOMDefault" caption="Receipt UOM Default" visible={false} />
            <Column dataField="recordNumber" caption="Record #" visible={false} />
            <Column dataField="itemUDFRef1" caption="Reference 1" visible={false} />
            <Column dataField="itemUDFRef2" caption="Reference 2" visible={false} />
            <Column dataField="itemUDFRef3" caption="Reference 3" visible={false} />
            <Column dataField="itemUDFRef4" caption="Reference 4" visible={false} />
            <Column dataField="itemUDFRef5" caption="Reference 5" visible={false} />
            <Column dataField="itemUDFRef6" caption="Reference 6" visible={false} />
            <Column dataField="itemUDFRef7" caption="Reference 7" visible={false} />
            <Column dataField="itemUDFRef8" caption="Reference 8" visible={false} />
            <Column dataField="itemUDFRef9" caption="Reference 9" visible={false} />
            <Column dataField="itemUDFRef10" caption="Reference 10" visible={false} />
            <Column dataField="shipmentPackSize" caption="Shipment Pack Size" visible={false} />
            <Column dataField="shipmentUOMDefault" caption="Shipment UOM Def" visible={false} />
            <Column dataField="itemOutUnAvailable" caption="Unavailable" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemGrossWeightQty" caption="Unit Gross Wt" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="itemNetWeightQty" caption="Unit Net Wt" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="weightBasisFlag" caption="Weight Basis Flg" visible={false} />
            <Toolbar>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerItems);
